import { t } from 'i18next';
import {
  Box,
  Button,
  Card,
  CardHeader,
  Checkbox,
  Divider,
  Grid,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Modal,
  Typography,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { useTransferMembers } from './hooks/useTransferMembers';
import { ClearIcon } from '@mui/x-date-pickers/icons';
import DiamondIcon from '@mui/icons-material/Diamond';
import { User } from '@common/types';
import { theme } from '@store';
import { Select } from 'ui';

interface ITransferMembersProps {
  teamId: string;
  teamColor: string | undefined;
  teamName: string | undefined;
}

const transferList = (
  teamTitle: React.ReactNode,
  teamIconColor: string | undefined,
  listItems: User[],
  rightTeamSelect: string,
  numberOfChecked: (listItems: User[]) => number,
  handleToggle: (value: User) => () => void,
  checked: User[],
  loading: boolean,
) => {
  const disabled = !rightTeamSelect || loading;
  return (
    <Card
      sx={{
        width: '100%',
        boxShadow: 'none',
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: 0,
      }}
    >
      <CardHeader
        sx={{ px: '16px', py: '12px', backgroundColor: theme.palette.grey[50] }}
        avatar={
          <DiamondIcon
            sx={{
              width: 24,
              height: 24,
              marginRight: 2,
              color: teamIconColor,
            }}
          />
        }
        title={teamTitle}
        subheader={`selected ${numberOfChecked(listItems)} of ${listItems.length}`}
      />
      <Divider />
      <List
        sx={{
          height: 340,
          bgcolor: 'background.paper',
          overflow: 'auto',
        }}
        dense
        component='div'
        role='list'
      >
        {listItems.map((value: User) => {
          const labelId = `transfer-list-all-item-${value.id}-label`;
          return (
            <ListItemButton
              key={value.id}
              role='listitem'
              onClick={handleToggle(value)}
              disabled={disabled}
            >
              <ListItemIcon>
                <Checkbox
                  checked={checked?.includes(value)}
                  tabIndex={-1}
                  disableRipple
                  slotProps={{
                    input: { 'aria-labelledby': labelId },
                  }}
                />
              </ListItemIcon>
              <ListItemText
                id={labelId}
                primary={`${value.first_name} ${value.last_name}`}
              />
              {value.lead && <StarIcon color='primary' />}
            </ListItemButton>
          );
        })}
      </List>
    </Card>
  );
};

export const TransferMembers = ({
  teamId,
  teamColor,
  teamName,
}: ITransferMembersProps) => {
  const {
    isOpen,
    handleClose,
    handleOnClick,
    handleToggle,
    numberOfChecked,
    handleCheckedRight,
    handleCheckedLeft,
    rightChecked,
    leftChecked,
    checked,
    leftTeamData,
    rightTeamData,
    filteredTeamNames,
    rightTeamId,
    handleSelectChange,
    loading,
  } = useTransferMembers({
    id: teamId,
  });

  return (
    <>
      <Button
        variant='outlined'
        sx={{
          width: '50%',
          fontWeight: 'medium',
          fontSize: '0.8rem',
          lineHeight: '22px',
        }}
        onClick={handleOnClick}
      >
        {t('adminPanel.teams.drawer.transferMembers')}
      </Button>
      <Modal
        open={isOpen}
        onClose={handleClose}
        sx={{ display: 'grid', placeItems: 'center' }}
      >
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: 24,
            height: 'fit-content',
            borderRadius: 0,
            backgroundColor: 'white',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              position: 'relative',
              justifyContent: 'space-between',
              width: '100%',
              padding: '0',
              pb: '1rem',
            }}
          >
            <Typography variant='h6' component='h2'>
              {t('adminPanel.teams.transferMemberModal.title')}
            </Typography>
            <Box
              onClick={handleClose}
              sx={{
                justifyContent: 'center',
                alignItems: 'center',
                display: 'flex',
                ':hover': {
                  cursor: 'pointer',
                  bgcolor: theme => theme.palette.grey[50],
                  borderRadius: '6px',
                },
                height: '30px',
                width: '30px',
              }}
            >
              <ClearIcon />
            </Box>
          </Box>
          <Grid
            container
            spacing={2}
            sx={{ justifyContent: 'center', alignItems: 'center' }}
          >
            <Grid sx={{ width: '380px' }}>
              {transferList(
                teamName,
                teamColor,
                leftTeamData.users,
                rightTeamId,
                numberOfChecked,
                handleToggle,
                checked,
                loading,
              )}
            </Grid>
            <Grid component={'div'}>
              <Grid
                container
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Button
                  sx={{ my: 0.5 }}
                  variant='outlined'
                  size='small'
                  onClick={handleCheckedRight}
                  disabled={leftChecked.length === 0}
                  aria-label='move selected right'
                >
                  &gt;
                </Button>
                <Button
                  sx={{ my: 0.5 }}
                  variant='outlined'
                  size='small'
                  onClick={handleCheckedLeft}
                  disabled={rightChecked.length === 0}
                  aria-label='move selected left'
                >
                  &lt;
                </Button>
              </Grid>
            </Grid>
            <Grid sx={{ width: '380px' }}>
              {rightTeamId && rightTeamData ? (
                transferList(
                  rightTeamData.name,
                  rightTeamData.color,
                  rightTeamData.users,
                  rightTeamId,
                  numberOfChecked,
                  handleToggle,
                  checked,
                  loading,
                )
              ) : (
                <Select
                  name='team'
                  label='Select team'
                  variant='outlined'
                  value={rightTeamId}
                  onChange={handleSelectChange}
                  options={filteredTeamNames}
                />
              )}
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </>
  );
};
